import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Loader2, Check, X as XIcon } from "lucide-react";

async function cropToBlob(file, area, maxW) {
  const url = URL.createObjectURL(file);
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = url;
  });
  const scale = Math.min(1, maxW / area.width);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(area.width * scale);
  canvas.height = Math.round(area.height * scale);
  canvas.getContext("2d").drawImage(
    img, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height);
  URL.revokeObjectURL(url);
  return new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.9));
}

export const ImageCropModal = ({ file, aspect = 1200 / 630, aspects = null, title = "Frame your image", maxWidth = 1600, onCancel, onCropped }) => {
  const [imgUrl] = useState(() => URL.createObjectURL(file));
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [asp, setAsp] = useState(aspect);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);
  const onComplete = useCallback((_a, px) => setArea(px), []);

  const close = () => { URL.revokeObjectURL(imgUrl); onCancel(); };
  const confirm = async () => {
    if (!area) return;
    setBusy(true);
    try {
      const blob = await cropToBlob(file, area, maxWidth);
      URL.revokeObjectURL(imgUrl);
      onCropped(blob);
    } catch {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" data-testid="image-crop-modal">
      <div className="w-full max-w-xl rounded-2xl border border-line bg-bg-elevated p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber">// {title}</div>
          <button type="button" onClick={close} className="text-ink-muted hover:text-ink-primary" data-testid="crop-cancel-btn" aria-label="Cancel crop">
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="relative w-full h-[340px] rounded-lg overflow-hidden bg-black">
          <Cropper image={imgUrl} crop={crop} zoom={zoom} aspect={asp}
            onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />
        </div>
        {aspects && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="crop-aspect-row">
            <span className="font-mono text-[9px] uppercase tracking-wider text-ink-muted mr-1">Frame</span>
            {aspects.map(([label, v]) => (
              <button key={label} type="button" onClick={() => setAsp(v)}
                data-testid={`crop-aspect-${label.toLowerCase().replace(/[^a-z0-9]/g, "")}`}
                className={`rounded-full border px-2.5 py-1 text-[10px] font-bold transition ${
                  asp === v ? "bg-brand-amber text-black border-brand-amber" : "border-line text-ink-secondary hover:text-ink-primary"}`}>
                {label}
              </button>
            ))}
          </div>
        )}
        <div className="mt-3 flex items-center gap-3">
          <span className="font-mono text-[9px] uppercase tracking-wider text-ink-muted">Zoom</span>
          <input type="range" min={1} max={3} step={0.05} value={zoom} data-testid="crop-zoom-slider"
            onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-[#f59e0b]" />
        </div>
        <p className="mt-2 text-[10px] text-ink-muted">
          Drag to position · scroll or slide to zoom. The frame matches exactly how it will appear once published.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={close}
            className="rounded-md border border-line px-4 py-2 text-xs font-semibold text-ink-secondary hover:text-ink-primary">
            Cancel
          </button>
          <button type="button" onClick={confirm} disabled={busy || !area} data-testid="crop-confirm-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Use this crop
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
